import * as Sentry from '@sentry/browser';
import LogRocket from 'logrocket';
import { sessionStorageAvailable } from './sessionStorage';
import { localStorageAvailable } from './localStorage';
import { AppConstants, PAGE_CATEGORY } from './constants';
import logInDev from './logger';
import { getSegmentId, waitForSegment } from './segment';
import * as Tracker from './tracker';

export const trackPageView = async ({ pageName, referrer }) => {
  const analytics = await waitForSegment();
  if (analytics) {
    analytics.page(PAGE_CATEGORY, pageName, {
      referrer,
    });
  }
  Tracker.trackPageView({ referrer });
  logInDev(`Page view: ${pageName}`);
  logInDev(`Previous page: ${referrer}`);
};

export const identifyAnalytics = async ({ ...props }) => {
  const analytics = await waitForSegment();
  // eslint-disable-next-line camelcase
  const segment_id = await getSegmentId();
  if (analytics) {
    analytics.identify(segment_id, { ...props, segment_id });
  }
  LogRocket.identify(segment_id, { ...props, segment_id });
  Sentry.setUser({ segment_id, ...props });
};

export const trackFormSubmission = async ({ data, message, pageName, pageNumber, submitText }) => {
  const analytics = await waitForSegment();
  let logMessage;
  const logPageName = pageName ?? 'Unknown';
  if (message) {
    logMessage = `[${logPageName}] ${message}`;
  } else {
    logMessage = `[${logPageName}] No message provided. Duplicate submission attempt.`;
  }
  // eslint-disable-next-line camelcase
  const segment_id = await getSegmentId();

  // not tracking individual form panel submission in segement
  if (analytics && pageNumber === AppConstants.totalPages) {
    analytics.track(message, {
      form_type: 'Application Form',
      label: submitText,
    });
  }

  LogRocket.track(logMessage, {
    pageName: logPageName,
    pageNumber,
  });

  LogRocket.identify(segment_id, {
    ...data,
  });

  Sentry.addBreadcrumb({
    category: 'prospectForm',
    message: logMessage,
    level: Sentry.Severity.Warn,
    data,
    segment_id,
  });
};

export const trackFormError = async ({ err, res, formName, message }) => {
  const errorObject = err ?? 'undefined';
  const pardotUrl = res?.url ?? 'undefined';
  const requestUrl = res?.requestUrl ?? 'undefined';
  const errMessage = res?.message ?? 'undefined';
  const captureMessage = message ?? `Error submitting ${formName} to Pardot [v3]`;
  // eslint-disable-next-line camelcase
  const segment_id = await getSegmentId();

  LogRocket.captureException(errorObject, {
    tags: {
      captureMessage,
      errMessage,
      pardotUrl,
      requestUrl,
    },
    extra: {
      errMessage,
      pardotUrl,
      requestUrl,
      segment_id,
    },
  });

  Sentry.withScope((scope) => {
    scope.setExtra('pardotUrl', pardotUrl);
    scope.setExtra('callbackName', formName);
    scope.setExtra('requestUrl', requestUrl);
    scope.setExtra('errorMessage', errMessage);
    scope.setExtra('errorObject', errorObject);
    scope.setExtra('localStorageAvailable', localStorageAvailable());
    scope.setExtra('sessionStorageAvailable', sessionStorageAvailable());
    scope.setExtra('sessionURL', LogRocket.sessionURL);
    scope.setExtra('segment_id', segment_id);
    Sentry.captureMessage(captureMessage, Sentry.Severity.Error);
  });
};

export const trackEvent = async ({ message }) => {
  const analytics = await waitForSegment();
  analytics.track(message);
};
